// 評估結果解析器 TypeScript 版本
import { normalizeCorrection } from "./correctionFormat";

interface EvaluationCategory {
  name: string;
  score: number;
  maxScore: number;
  comment: string;
  grade?: string;
}

interface EvaluationResult {
  totalScore: number;
  maxPossibleScore: number;
  totalGrade: string;
  overallComment: string;
  suggestions: string[];
  wordCount: string;
  categories: EvaluationCategory[];
  uploadMode: string;
  markdownTable?: string;
  error: string | null;
}

// 評分方案設定，方便擴充與維護
interface SchemeConfig {
  name: string;
  // 以分類名稱作為 key，值為滿分
  categoryMaxScores: Record<string, number>;
  totalMaxScore: number;
}

// 既有 25 分制（高中舊格式）
const SCHEME_25: SchemeConfig = {
  name: "25-score",
  categoryMaxScores: {
    題旨發揮: 10,
    資料掌握: 5,
    結構安排: 5,
    字句應用: 5,
  },
  totalMaxScore: 25,
};

// 新制方案（分類名稱與滿分可依需求調整）
const SCHEME_NEW: SchemeConfig = {
  name: "new-score",
  categoryMaxScores: {
    立意取材: 8,
    結構組織: 6,
    遣詞造句: 6,
    "錯別字、格式與標點符號": 4,
  },
  totalMaxScore: 24,
};

// 方案列表，日後新增直接在此擴充
const SCHEMES: SchemeConfig[] = [SCHEME_25, SCHEME_NEW];

interface OverallScore {
  總分?: number;
  滿分?: number;
  評語?: string;
  建議?: string[];
  等第?: string;
}

interface JsonEvaluationData {
  整體評分?: OverallScore;
  題旨發揮?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  資料掌握?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  結構安排?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  字句應用?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  立意取材?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  結構組織?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  遣詞造句?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
  "錯別字、格式與標點符號"?: {
    得分?: number;
    評語?: string;
    等第?: string;
  };
}

const CATEGORY_MAX_SCORE_FALLBACK: Record<string, number> = {
  題旨發揮: 10,
  資料掌握: 5,
  結構安排: 5,
  字句應用: 5,
  立意取材: 8,
  結構組織: 6,
  遣詞造句: 6,
  "錯別字、格式與標點符號": 4,
};

// interface WorkflowResult {
//   originalResponse?: string | JsonEvaluationData;
//   oneAnswer?: string;
//   data?: {
//     outputs?: {
//       oneAnswer?: string;
//     };
//   };
// }

/**
 * 通用評估結果解析器
 * 根據上傳模式決定是否生成表格
 * 新增對 originalResponse 格式的支援 (高中作文批改 API)
 * @param workflowResult - 工作流結果對象
 * @param uploadMode - 上傳模式，"image" 或 "text"
 * @returns 結構化的評估數據
 */
// ── 新評語輸出格式(kanban 1000517)────────────────────────────────────────
// 2026-08-26 起各書 prompt 逐本改用 totalScore / categories / overallComment,
// 沒有「整體評分」鍵。這支解析器有 5 個分派點,全都以「有沒有整體評分」為條件,
// 逐一改判斷容易漏。改採入口正規化:把新格式攤回既有形狀,下游一律不動。
//
// 攤平後各項滿分交給既有的 scheme 表決定(與新格式帶的 maxScore 相同:題旨發揮 10、
// 其餘各 5)。新格式**沒有等第** —— 舊 prompt 的「等第」在新 prompt 裡不存在,
// 所以攤平後等第一律空字串,畫面上該欄會是空的,這是 prompt 設計的結果、非解析失敗。
export const isNewCommentFormat = (data: unknown): boolean => {
  if (!data || typeof data !== "object") return false;
  const obj = data as { overallComment?: unknown; categories?: unknown };
  return (
    (typeof obj.overallComment === "object" && obj.overallComment !== null) ||
    Array.isArray(obj.categories)
  );
};

const adaptNewCommentFormat = (data: unknown): unknown => {
  if (!isNewCommentFormat(data)) return data;
  const obj = data as {
    totalScore?: unknown;
    totalGrade?: unknown;
    categories?: unknown;
    overallComment?: { comment?: unknown; suggestions?: unknown };
  };
  const normalized = normalizeCorrection(obj);
  if (!normalized) return data;
  const adapted: Record<string, unknown> = {};
  for (const category of normalized.categories) {
    if (!category.name) continue;
    adapted[category.name] = {
      等第: category.grade ?? "",
      得分: category.score,
      評語: category.comment,
    };
  }
  const maxSum = normalized.categories.reduce(
    (sum, c) => sum + (c.maxScore ?? 0),
    0
  );
  adapted["整體評分"] = {
    等第: normalized.totalGrade ?? "",
    總分: normalized.totalScore,
    滿分: maxSum || undefined,
    評語: normalized.overallComment,
    建議: normalized.suggestions,
  };
  return adapted;
};

export default function parseEvaluationResult(
  workflowResult: any,
  uploadMode: string = "image"
): EvaluationResult {
  try {
    // 創建結果對象
    const result: EvaluationResult = {
      totalScore: 0,
      maxPossibleScore: 25,
      totalGrade: "",
      overallComment: "",
      suggestions: [],
      wordCount: "",
      categories: [],
      uploadMode: uploadMode,
      error: null,
    };

    // 只在 image 模式下才初始化表格屬性
    if (uploadMode === "image") {
      result.markdownTable = "";
    }

    // 1. 檢查是否有 originalResponse 屬性，可能為 JSON 字串或對象
    if (
      typeof workflowResult === "object" &&
      workflowResult !== null &&
      workflowResult.originalResponse
    ) {
      const originalResponse = workflowResult.originalResponse;

      // 嘗試解析 originalResponse（如果它是 JSON 字串包裹在 ```json 中）
      if (typeof originalResponse === "string") {
        let cleanedResponse = originalResponse;

        // 移除 Markdown 程式碼區塊標記
        if (cleanedResponse.startsWith("```json") && cleanedResponse.endsWith("```")) {
          cleanedResponse = cleanedResponse
            .replace(/^```json\n/, "")
            .replace(/\n```$/, "");
        }

        try {
          // 嘗試解析為 JSON
          const parsedData = JSON.parse(cleanedResponse);

          // 檢查是否包含新 API 格式的特定欄位
          if (parsedData.整體評分 || isNewCommentFormat(parsedData)) {
            return parseNewJsonFormat(parsedData, uploadMode);
          } else {
            // 檢查是否是舊格式 JSON
            const hasTextModeKeys =
              parsedData.題旨發揮 ||
              parsedData.資料掌握 ||
              parsedData.結構安排 ||
              parsedData.字句應用 ||
              parsedData.立意取材 ||
              parsedData.結構組織 ||
              parsedData.遣詞造句 ||
              parsedData["錯別字、格式與標點符號"] ||
              parsedData.整體評分;

            if (hasTextModeKeys) {
              return parseJsonFormat(parsedData, uploadMode);
            } else {
              // 當作文本處理
              return parseTextFormat(cleanedResponse, uploadMode);
            }
          }
        } catch {
          // JSON 解析失敗，先檢查是否為錯誤訊息，再用文本解析
          if (isErrorMessage(originalResponse)) {
            return createErrorResult(originalResponse, uploadMode);
          }
          return parseTextFormat(originalResponse, uploadMode);
        }
      }
    }

    // 2. 檢查是否為 JSON 格式 (text 模式)
    if (
      typeof workflowResult === "object" &&
      workflowResult !== null &&
      !workflowResult.oneAnswer
    ) {
      // 檢查是否包含評分類別或整體評分鍵
      const hasTextModeKeys =
        workflowResult.題旨發揮 ||
        workflowResult.資料掌握 ||
        workflowResult.結構安排 ||
        workflowResult.字句應用 ||
        workflowResult.立意取材 ||
        workflowResult.結構組織 ||
        workflowResult.遣詞造句 ||
        workflowResult["錯別字、格式與標點符號"] ||
        workflowResult.整體評分;

      if (hasTextModeKeys) {
        return parseJsonFormat(workflowResult, uploadMode);
      }
      
      // 檢查是否包含新 API 格式的特定欄位
      if (workflowResult.整體評分) {
        return parseNewJsonFormat(workflowResult, uploadMode);
      }
    }

    // 3. 從工作流結果中提取評估內容 (image 模式或其他)
    let evaluationText = "";

    // 處理不同的輸入格式
    if (typeof workflowResult === "string") {
      try {
        // 嘗試解析 JSON 字符串
        const parsed = JSON.parse(workflowResult);

        // 檢查新 API 格式 (有 originalResponse 屬性)
        if (parsed.originalResponse) {
          // 嘗試再次解析 originalResponse
          if (typeof parsed.originalResponse === "string") {
            let cleanedResponse = parsed.originalResponse;
            
            // 移除 Markdown 程式碼區塊標記
            if (cleanedResponse.startsWith("```json") && cleanedResponse.endsWith("```")) {
              cleanedResponse = cleanedResponse
                .replace(/^```json\n/, "")
                .replace(/\n```$/, "");
              
              try {
                const nestedJson = JSON.parse(cleanedResponse);
                if (nestedJson.整體評分) {
                  return parseNewJsonFormat(nestedJson, uploadMode);
                }
              } catch {
                // 解析失敗，繼續使用文本方式
              }
            }
            
            evaluationText = parsed.originalResponse;
            
            // 檢查是否為無法識別圖片的錯誤訊息
            if (isErrorMessage(evaluationText)) {
              return createErrorResult(evaluationText, uploadMode);
            }
          }
        } else if (parsed.oneAnswer) {
          evaluationText = parsed.oneAnswer;

          // 檢查是否為無法識別圖片的錯誤訊息
          if (isErrorMessage(evaluationText)) {
            return createErrorResult(evaluationText, uploadMode);
          }
        } else if (
          !Array.isArray(parsed) &&
          typeof parsed === "object" &&
          (parsed.題旨發揮 ||
            parsed.資料掌握 ||
            parsed.結構安排 ||
            parsed.字句應用 ||
            parsed.立意取材 ||
            parsed.結構組織 ||
            parsed.遣詞造句 ||
            parsed["錯別字、格式與標點符號"] ||
            parsed.整體評分)
        ) {
          return parseJsonFormat(parsed as JsonEvaluationData, uploadMode);
        } else if (
          !Array.isArray(parsed) &&
          typeof parsed === "object" &&
          parsed.整體評分
        ) {
          return parseNewJsonFormat(parsed as JsonEvaluationData, uploadMode);
        } else {
          evaluationText = workflowResult;
        }
      } catch {
        // 如果不是有效的 JSON，直接使用原字符串
        evaluationText = workflowResult;
      }
    } else if (workflowResult && workflowResult.oneAnswer) {
      // 直接從對象中獲取 oneAnswer
      evaluationText = workflowResult.oneAnswer;

      // 檢查是否為無法識別圖片的錯誤訊息
      if (isErrorMessage(evaluationText)) {
        return createErrorResult(evaluationText, uploadMode);
      }
    } else if (
      workflowResult &&
      workflowResult.data &&
      workflowResult.data.outputs &&
      workflowResult.data.outputs.oneAnswer
    ) {
      try {
        let cleanedOneAnswer = workflowResult.data.outputs.oneAnswer;

        // 檢查是否為無法識別圖片的錯誤訊息
        if (isErrorMessage(cleanedOneAnswer)) {
          return createErrorResult(cleanedOneAnswer, uploadMode);
        }

        if (
          cleanedOneAnswer.startsWith("```json") &&
          cleanedOneAnswer.endsWith("```")
        ) {
          cleanedOneAnswer = cleanedOneAnswer
            .replace(/^```json\n/, "")
            .replace(/\n```$/, "");
        }

        // 首先嘗試使用 JSON.parse
        try {
          const parsedOneAnswer = JSON.parse(cleanedOneAnswer);
          
          // 檢查是否包含新 API 格式的特定欄位
          if (parsedOneAnswer.整體評分) {
            return parseNewJsonFormat(parsedOneAnswer, uploadMode);
          } else {
            return parseJsonFormat(parsedOneAnswer, uploadMode);
          }
        } catch {
          // 如果 JSON 解析失敗，直接使用文本解析
          return parseTextFormat(cleanedOneAnswer, uploadMode);
        }
      } catch {
        return createEmptyResult(uploadMode);
      }
    }

    // 無法獲取評估文本，返回空結果
    if (!evaluationText) {
      return createEmptyResult(uploadMode);
    }

    // 檢查是否為無法識別圖片的錯誤訊息
    if (isErrorMessage(evaluationText)) {
      return createErrorResult(evaluationText, uploadMode);
    }

    // 解析文本格式 (通常是 image 模式)
    return parseTextFormat(evaluationText, uploadMode);
  } catch (error) {
    console.error("解析評估結果時發生錯誤:", error);
    return createEmptyResult(uploadMode);
  }
}

/**
 * 解析新的 JSON 格式 (只有 整體評分)
 * @param jsonData - JSON 格式的評估數據
 * @param uploadMode - 上傳模式
 * @returns 標準化的評估結果
 */
function parseNewJsonFormat(jsonData: JsonEvaluationData, uploadMode: string): EvaluationResult {
  try {
    // 五個分派點的共同出口:新格式在這裡攤回既有形狀(kanban 1000517)
    jsonData = adaptNewCommentFormat(jsonData) as JsonEvaluationData;
    const scheme = detectScheme(jsonData) || SCHEME_25;
    const result: EvaluationResult = {
      totalScore: 0,
      maxPossibleScore: scheme.totalMaxScore,
      totalGrade: "",
      overallComment: "",
      suggestions: [],
      wordCount: "",
      categories: [],
      uploadMode: uploadMode,
      error: null,
    };

    // 提取整體評分
    if (jsonData.整體評分) {
      // 設置總分和滿分
      result.totalScore = jsonData.整體評分.總分 || 0;
      result.maxPossibleScore = jsonData.整體評分.滿分 || scheme.totalMaxScore;
      
      // 設置評語
      result.overallComment = jsonData.整體評分.評語 || "";
      
      // 設置建議
      if (jsonData.整體評分.建議 && Array.isArray(jsonData.整體評分.建議)) {
        result.suggestions = jsonData.整體評分.建議;
      }
      
      // 直接使用 API 傳來的等第，不重新計算
      result.totalGrade = jsonData.整體評分.等第 || "";
    }

    // 提取分類評分
    const categoryKeys: (keyof JsonEvaluationData)[] = [
      "題旨發揮",
      "資料掌握",
      "結構安排",
      "字句應用",
      "立意取材",
      "結構組織",
      "遣詞造句",
      "錯別字、格式與標點符號",
    ];
    categoryKeys.forEach((key) => {
      const typedKey = key as keyof JsonEvaluationData;
      if (jsonData[typedKey] && key !== "整體評分") {
        const categoryData = jsonData[typedKey] as { 得分?: number; 評語?: string; 等第?: string; };
        const category: EvaluationCategory = {
          name: key,
          score: parseFloat(String(categoryData.得分)) || 0,
          maxScore: CATEGORY_MAX_SCORE_FALLBACK[key as string] || 0,
          comment: categoryData.評語 || "",
          grade: categoryData.等第 || "",
        };
        result.categories.push(category);
      }
    });

    // 只在 image 模式下才生成表格
    if (uploadMode === "image") {
      // 如果沒有分類資料，則用整體評分建立一個簡單表格
      const simplifiedCategory: EvaluationCategory = {
        name: "整體評分",
        score: result.totalScore,
        maxScore: result.maxPossibleScore,
        comment: result.overallComment,
        grade: result.totalGrade,
      };
      
      // 加入到類別中
      result.categories.push(simplifiedCategory);
      
      // 生成表格
      result.markdownTable = generateMarkdownTable([simplifiedCategory]);
    }

    return result;
  } catch (error) {
    console.error("解析新 JSON 格式時發生錯誤:", error);
    return createEmptyResult(uploadMode);
  }
}

/**
 * 檢查文本是否為無法識別圖片的錯誤訊息
 * @param text - 要檢查的文本
 * @returns 是否為錯誤訊息
 */
function isErrorMessage(text: string): boolean {
  if (!text) return false;

  // 如果文字包含有效的評分 JSON 結構，則不是錯誤訊息
  // （避免 AI 評語中的關鍵字誤觸發，例如「可修飾為『無法辨識』」）
  const validResultPatterns = [
    /["']整體評分["']/,
    /["']題旨發揮["']/,
    /["']立意取材["']/,
    /["']得分["']/,
  ];
  if (validResultPatterns.some((pattern) => pattern.test(text))) {
    return false;
  }

  // 真正的錯誤訊息通常很短，有效批改結果通常很長
  // 超過 500 字元的文字幾乎不可能是純錯誤訊息
  if (text.length > 500) {
    return false;
  }

  // 檢查常見的錯誤訊息關鍵字
  const errorPatterns = [
    /請提供包含完整內容的圖片/,
    /如果圖片有問題/,
    /請確認是否清晰可讀/,
    /無法辨識/,
    /無法識別/,
    /看不清楚/,
    /圖片不清晰/,
    /請重新上傳/,
    /若發生無法讀取/,
    /上傳圖片必須轉正/,
    /重新拍照/,
    /掃描方式轉錄圖片/,
  ];

  return errorPatterns.some((pattern) => pattern.test(text));
}

/**
 * 創建包含錯誤訊息的結果對象
 * @param errorMessage - 錯誤訊息
 * @param uploadMode - 上傳模式
 * @returns 包含錯誤訊息的結果對象
 */
function createErrorResult(errorMessage: string, uploadMode: string): EvaluationResult {
  const result = createEmptyResult(uploadMode);
  result.error = errorMessage;
  result.overallComment = errorMessage;

  return result;
}

/**
 * 解析 JSON 格式的評估結果 (text 模式)
 * @param jsonData - JSON 格式的評估數據
 * @param uploadMode - 上傳模式
 * @returns 標準化的評估結果
 */
function parseJsonFormat(jsonData: JsonEvaluationData, uploadMode: string): EvaluationResult {
  try {
    const scheme = detectScheme(jsonData) || SCHEME_25;
    const result: EvaluationResult = {
      totalScore: 0,
      maxPossibleScore: scheme.totalMaxScore,
      totalGrade: "",
      overallComment: "",
      suggestions: [],
      wordCount: "",
      categories: [],
      uploadMode: uploadMode,
      error: null,
    };

    // 提取整體評分
    if (jsonData.整體評分) {
      result.totalScore = jsonData.整體評分.總分 || 0;
      result.maxPossibleScore = jsonData.整體評分.滿分 || scheme.totalMaxScore;

      // 如果沒有等第，且沒有分類評分，則根據總分計算等第
      if (!result.totalGrade && result.categories.length === 0) {
        result.totalGrade = calculateGrade(result.totalScore, result.maxPossibleScore);
      }

      result.overallComment = jsonData.整體評分.評語 || "";

      if (jsonData.整體評分.建議 && Array.isArray(jsonData.整體評分.建議)) {
        result.suggestions = jsonData.整體評分.建議;
      }
    }

    // 提取分類評分
    const categoryKeys: (keyof JsonEvaluationData)[] = [
      "題旨發揮",
      "資料掌握",
      "結構安排",
      "字句應用",
      "立意取材",
      "結構組織",
      "遣詞造句",
      "錯別字、格式與標點符號",
    ];

    categoryKeys.forEach((key) => {
      const typedKey = key as keyof JsonEvaluationData;
      if (jsonData[typedKey] && key !== "整體評分") {
        const categoryData = jsonData[typedKey] as { 得分?: number; 評語?: string; 等第?: string; };
        const category: EvaluationCategory = {
          name: key,
          score: parseFloat(String(categoryData.得分)) || 0,
          maxScore: CATEGORY_MAX_SCORE_FALLBACK[key as string] || 0,
          comment: categoryData.評語 || "",
          grade: categoryData.等第 || "",
        };
        result.categories.push(category);
      }
    });

    // 只在 image 模式下才生成表格
    if (uploadMode === "image") {
      result.markdownTable = generateMarkdownTable(result.categories);
    }

    return result;
  } catch (error) {
    console.error("解析 JSON 格式時發生錯誤:", error);
    return createEmptyResult(uploadMode);
  }
}

/**
 * 解析文本格式的評估結果 (image 模式)
 * @param text - 文本格式的評估內容
 * @param uploadMode - 上傳模式
 * @returns 標準化的評估結果
 */
function parseTextFormat(text: string, uploadMode: string): EvaluationResult {
  try {
    // 清理文本，確保換行符一致
    const cleanedText = text.replace(/\r\n/g, "\n");

    // 創建結果對象
    const result: EvaluationResult = {
      totalScore: 0,
      maxPossibleScore: 25,
      totalGrade: "",
      overallComment: "",
      suggestions: [],
      wordCount: "",
      categories: [],
      uploadMode: uploadMode,
      error: null,
    };

    // 只在 image 模式下初始化表格屬性
    if (uploadMode === "image") {
      result.markdownTable = "";
    }

    // 提取字數檢查
    const wordCountMatch = cleanedText.match(
      /【字數檢查與提醒】\s*([\s\S]*?)(?=\n\n【|\n$)/
    );
    if (wordCountMatch && wordCountMatch[1]) {
      result.wordCount = wordCountMatch[1].trim();
    }

    // 提取分數評定部分
    const scoreSectionMatch = cleanedText.match(
      /【分數評定】\s*([\s\S]*?)(?=\n\n【|\n$)/
    );
    if (scoreSectionMatch && scoreSectionMatch[1]) {
      const scoreSection = scoreSectionMatch[1].trim();

      // 提取類別評分 (題旨發揮、資料掌握、結構安排、字句應用)
      const categoryPattern =
        /- ([^：]+)：(\d+(?:\.\d+)?)\s*分\s*\/\s*(\d+)分\s*([\s\S]*?)(?=\n\n-|\n\n 總分|\n$)/g;
      let categoryMatch;

      while ((categoryMatch = categoryPattern.exec(scoreSection)) !== null) {
        const categoryName = categoryMatch[1].trim();
        const categoryScore = parseFloat(categoryMatch[2]);

        result.categories.push({
          name: categoryName,
          score: categoryScore,
          maxScore: parseInt(categoryMatch[3], 10),
          comment: categoryMatch[4].trim(),
        });
      }

      // 提取總分和等第
      const totalScoreMatch = scoreSection.match(
        /總分：(\d+(?:\.\d+)?)\s*\/\s*(\d+)\s*分/
      );
      if (totalScoreMatch) {
        result.totalScore = parseFloat(totalScoreMatch[1]);
        result.maxPossibleScore = parseInt(totalScoreMatch[2], 10);
      }

      const gradeMatch = scoreSection.match(/等第：([A-D][+-]?)級/);
      if (gradeMatch) {
        result.totalGrade = gradeMatch[1]; // 移除"級"字
      }
    }

    // 只在 image 模式下處理表格
    if (uploadMode === "image") {
      // 提取 Markdown 表格
      const tableRegex =
        /\|\s*評分項目\s*\|\s*分數\s*\|\s*評語\s*\|\n\|[-]+\|[-]+\|[-]+\|([\s\S]*?)(?=\n\n|\n【|\n$)/;
      const tableMatch = cleanedText.match(tableRegex);

      if (tableMatch) {
        // 完整表格包括表頭、分隔行和數據行
        const fullTableHeader =
          "| 評分項目 | 分數 | 評語 |\n|----------|----------------|------|";
        const tableData = tableMatch[1].trim();

        // 組合完整表格
        result.markdownTable = `${fullTableHeader}\n${tableData}`;
      }
      // 如果沒有找到表格，但有類別數據，則生成表格
      else if (result.categories.length > 0) {
        result.markdownTable = generateMarkdownTable(result.categories);
      }
    }

    // 提取整體評語和建議
    const commentSectionMatch = cleanedText.match(
      /【整體評語與建議】\s*([\s\S]*?)(?=$)/
    );
    if (commentSectionMatch && commentSectionMatch[1]) {
      const commentSection = commentSectionMatch[1].trim();

      // 提取總評
      const overallCommentMatch = commentSection.match(
        /- 總評\s*([\s\S]*?)(?=\n\n-|\n$)/
      );
      if (overallCommentMatch && overallCommentMatch[1]) {
        result.overallComment = overallCommentMatch[1].trim();
      }

      const suggestionsMatch = commentSection.match(
        /- 寫作建議\s*([\s\S]*?)(?=\n\n-|\n$|$)/
      );
      if (suggestionsMatch && suggestionsMatch[1]) {
        const suggestionsText = suggestionsMatch[1].trim();

        // 正則表達式修改，匹配每一條帶序號的建議
        const suggestionPattern = /(\d+\.\s*[^\d\n]+)/g;
        const matches = suggestionsText.match(suggestionPattern);

        if (matches && matches.length > 0) {
          // 直接使用匹配到的完整建議（含序號）
          result.suggestions = matches.map((s) => s.trim());
        } else {
          // 後備處理：如果正則匹配失敗，則使用整個文本
          result.suggestions = [suggestionsText.trim()];
        }
      }
    }

    // 計算總分（如果尚未設置）
    if (result.totalScore === 0 && result.categories.length > 0) {
      result.totalScore = result.categories.reduce(
        (sum, category) => sum + category.score,
        0
      );
    }

    // 計算等第（如果尚未設置）
    if (!result.totalGrade && result.totalScore > 0) {
      result.totalGrade = calculateGrade(result.totalScore);
    }

    return result;
  } catch (error) {
    console.error("解析文本格式時發生錯誤:", error);
    return createEmptyResult(uploadMode);
  }
}

/**
 * 從類別數據生成 Markdown 表格
 * @param categories - 類別數據數組
 * @returns Markdown 表格
 */
function generateMarkdownTable(categories: EvaluationCategory[]): string {
  if (!categories || categories.length === 0) return "";

  const fullTableHeader =
    "| 評分項目 | 分數 | 評語 |\n|----------|----------------|------|";
  const tableRows = categories
    .map((category) => {
      const shortComment = getShortComment(category.comment);
      return `| ${category.name} | ${category.score} / ${category.maxScore}分 | ${shortComment} |`;
    })
    .join("\n");

  return `${fullTableHeader}\n${tableRows}`;
}

/**
 * 獲取評語的完整版本（移除字數限制）
 * @param comment - 完整評語
 * @returns 完整評語
 */
function getShortComment(comment: string): string {
  if (!comment) return "";
  
  // 直接返回完整評語，不再截斷
  return comment;
}

/**
 * 根據 jsonData 的分類鍵找出最適方案
 */
function detectScheme(jsonData: JsonEvaluationData | null | undefined): SchemeConfig | null {
  if (!jsonData) return null;
  const keys = new Set(Object.keys(jsonData));

  let bestScheme: SchemeConfig | null = null;
  let bestMatchCount = 0;

  for (const scheme of SCHEMES) {
    const schemeKeys = Object.keys(scheme.categoryMaxScores);
    const matchCount = schemeKeys.filter((k) => keys.has(k)).length;
    if (matchCount > bestMatchCount) {
      bestMatchCount = matchCount;
      bestScheme = scheme;
    }
  }

  return bestScheme;
}

/**
 * 根據總分和滿分計算等第
 * @param score - 總分
 * @param maxScore - 滿分
 * @returns 等第
 */

function calculateGrade(score: number, maxScore: number = SCHEME_25.totalMaxScore): string {
  // 使用比例判斷，保持與既有 25 分制的分段相近：A ≈ 72% 以上，B ≈ 40% 以上，C ≈ 1 以上
  const ratio = maxScore > 0 ? score / maxScore : 0;
  if (ratio >= 0.72) return "A";
  if (ratio >= 0.4) return "B";
  if (ratio > 0) return "C";
  return "0";
}

/**
 * 創建空的評估結果
 * @param uploadMode - 上傳模式
 * @returns 空的評估結果
 */
function createEmptyResult(uploadMode: string): EvaluationResult {
  const result: EvaluationResult = {
    totalScore: 0,
    maxPossibleScore: SCHEME_25.totalMaxScore,
    totalGrade: "",
    overallComment: "",
    suggestions: [],
    wordCount: "",
    categories: [],
    uploadMode: uploadMode,
    error: null,
  };

  // 只在 image 模式下初始化表格屬性
  if (uploadMode === "image") {
    result.markdownTable = "";
  }

  return result;
}
